import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/layout/Layout';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ServiceCategoriesPage from './pages/categories/ServiceCategoriesPage';
import ProfessionalServicesPage from './pages/services/ProfessionalServicesPage';
import SubscriptionPlansPage from './pages/subscriptions/SubscriptionPlansPage';
import LeadsPage from './pages/leads/LeadsPage';
import LeadStatisticsPage from './pages/leads/LeadStatisticsPage';
import ContactRevealEventsPage from './pages/events/ContactRevealEventsPage';
import PaymentDashboardPage from './pages/payments/PaymentDashboardPage';
import TransactionMonitoringPage from './pages/payments/TransactionMonitoringPage';
import PayoutApprovalPage from './pages/payments/PayoutApprovalPage';
import CommissionTrackingPage from './pages/payments/CommissionTrackingPage';
import FinancialReportingPage from './pages/payments/FinancialReportingPage';
import ProfessionalsPage from './pages/professionals/ProfessionalsPage';
import PatientsPage from './pages/patients/PatientsPage';
import SettingsPage from './pages/settings/SettingsPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import RoleManagementPage from './pages/admin/RoleManagementPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import CityManagementPage from './pages/admin/CityManagementPage';
import UnauthorizedPage from './pages/unauthorized/UnauthorizedPage';
import medicalTheme from './styles/medicalTheme';
import { NotificationProvider } from './contexts/NotificationContext';

const theme = createTheme(medicalTheme);

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <Navigate to="/dashboard" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.activity_logs.view', 'admin.admin_comments.manage']}>
                  <Layout>
                    <NotificationsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.service_categories.manage']}>
                  <Layout>
                    <ServiceCategoriesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/services"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.verification.manage']}>
                  <Layout>
                    <ProfessionalServicesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/professionals"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.users.view']}>
                  <Layout>
                    <ProfessionalsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.users.view']}>
                  <Layout>
                    <PatientsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription-plans"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.subscriptions.view', 'admin.subscriptions.manage']}>
                  <Layout>
                    <SubscriptionPlansPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.leads.monitor']}>
                  <Layout>
                    <LeadsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/statistics"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.leads.analytics.view']}>
                  <Layout>
                    <LeadStatisticsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-reveal-events"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.contact_reveal.view']}>
                  <Layout>
                    <ContactRevealEventsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.payments.view']}>
                  <Layout>
                    <PaymentDashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/transactions"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.payments.view']}>
                  <Layout>
                    <TransactionMonitoringPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/payouts"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.payments.manage']}>
                  <Layout>
                    <PayoutApprovalPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/commissions"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.payments.view']}>
                  <Layout>
                    <CommissionTrackingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/reports"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.payments.view']}>
                  <Layout>
                    <FinancialReportingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.system_settings.manage', 'admin.payment.settings.manage']}>
                  <Layout>
                    <SettingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.role_management.view', 'admin.user_roles.assign']}>
                  <Layout>
                    <RoleManagementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.users.view']}>
                  <Layout>
                    <UserManagementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cities"
              element={
                <ProtectedRoute requireAdmin requiredPermissions={['admin.system_settings.manage']}>
                  <Layout>
                    <CityManagementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
};

export default App;